import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Spinner, Input, Alert, Label,
  Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup,
} from "reactstrap";
import classnames from "classnames";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { fetchPlatformStats } from "../../../helpers/platformStats";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

// Sunucu taraflı toplu bildirim uçları (AdminBildirimController).
// Uç canlıda yoksa (henüz deploy edilmemişse) 404 döner ve sayfa
// eski "kullanıcı başına OData POST" akışına düşer.
const TOPLU_UC = "/api/AdminBildirim";

const getAuthHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Tıklanınca gidilecek ekranlar.
 * `v` değeri BILDIRIM_LOGLARI.EKRAN kolonuna yazılır ve push payload'ında
 * data.screen olur; mobil tarafta assets/Others/notificationConfig.js bunu
 * RootStack route'una çevirir. Buradaki adlar o dosyadaki
 * KNOWN_DIRECT_SCREENS / özel dallarla birebir uyumlu tutulmalı.
 * `idAlani` dolu olan ekranlar ILGILI_ID olmadan doğru kaydı açamaz.
 */
const EKRAN_SECENEKLERI = [
  { v: "", l: "— Bildirim listesi (varsayılan) —" },
  { v: "DegerleGel", l: "Değerle'GEL sekmesi (ana sayfa)" },
  { v: "ValuationDetail", l: "Değerleme talebi detayı", idAlani: "Talep ID" },
  { v: "ProductDetail", l: "İlan detayı", idAlani: "İlan ID" },
  { v: "LiveAuctionScreen", l: "Mezat / canlı yayın", idAlani: "Mezat ID" },
  { v: "StoreProfileScreen", l: "Mağaza profili", idAlani: "Mağaza ID" },
  { v: "MyOrdersScreen", l: "Siparişlerim", idAlani: "Sipariş ID" },
  { v: "BidsScreen", l: "Teklifler" },
  { v: "BuyRightsScreen", l: "Hak / paket satın alma" },
  { v: "HizmetTalepler", l: "Hizmet'eGEL talepleri" },
  { v: "VideoReelsScreen", l: "Video akışı" },
  { v: "AddBalanceScreen", l: "Cüzdan / bakiye" },
];

// TIP seçilince mantıklı varsayılan ekran (admin isterse üzerine yazar).
const TIP_VARSAYILAN_EKRAN = {
  Degerleme: "DegerleGel",
  DegerlemeSonuc: "DegerleGel",
  YeniDegerleme: "DegerleGel",
  Finans: "AddBalanceScreen",
};

/**
 * Bildirim Gönderme Paneli
 *
 * Akış (yeni):
 *  - Panel tek bir POST atar: api/AdminBildirim/TopluGonder.
 *  - Sunucu hedef kitleyi çözer (silinmiş hesaplar hariç), BILDIRIM_LOGLARI
 *    satırlarını toplu INSERT ile yazar ve FCM push'u arka plana kuyruklar.
 *  - Sayfa hemen yanıt alır; sekme kapansa da gönderim sürer.
 *
 * Geriye dönük uyumluluk:
 *  - Uç 404 dönerse (API henüz deploy edilmemişse) eski akışa düşülür:
 *    hedefteki her KULLANICI_ID için ayrı OData POST + BILDIRIM_LOGLARIController.Post
 *    içindeki tekil push.
 */
const BildirimGonder = () => {
  document.title = "Bildirim Yönetimi | HarmanaGEL";

  const [activeTab, setActiveTab] = useState("compose");
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // Form state
  const [hedefTip, setHedefTip] = useState("all"); // all | user | category | tokenholders
  const [aramaSorgu, setAramaSorgu] = useState("");
  const [aramaSonuc, setAramaSonuc] = useState([]);
  const [aramaLoading, setAramaLoading] = useState(false);
  const [seciliKullanici, setSeciliKullanici] = useState(null);
  const [kategoriler, setKategoriler] = useState([]);
  const [seciliKategoriId, setSeciliKategoriId] = useState("");
  const [baslik, setBaslik] = useState("");
  const [mesaj, setMesaj] = useState("");
  // BildirimTipleri.cs'deki standart kodlarla uyumlu — mobil/web NotificationsScreen ikon mapping'i bu kodları tanır.
  // SistemBildirim default; promosyon/duyuru için YeniIcerik kullanılır.
  const [bildirimTipi, setBildirimTipi] = useState("SistemBildirim");
  // EKRAN: BILDIRIM_LOGLARI.EKRAN kolonu. Backend push data.screen olarak yollar,
  // mobil assets/Others/notificationConfig.js bu adı route'a çevirir. Boş bırakılırsa
  // bildirim listesi (NotificationsScreen) açılır.
  const [ekran, setEkran] = useState("");
  // ILGILI_ID: ekran bir kayıt detayı ise (ilan, mezat, sipariş, mağaza) hedef kaydın ID'si.
  const [ilgiliId, setIlgiliId] = useState("");

  const [previewCount, setPreviewCount] = useState(null);
  // Hedefteki kişi sayısı ile gerçekten push alacak cihaz sayısı farklıdır
  // (token'ı olmayan kullanıcıya yalnızca uygulama içi bildirim düşer).
  const [previewTokenCount, setPreviewTokenCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Geçmiş tab
  const [gecmis, setGecmis] = useState([]);
  const [gecmisLoading, setGecmisLoading] = useState(false);

  // Token istatistikleri
  const [tokenStats, setTokenStats] = useState({
    total: 0, web: 0, android: 0, ios: 0, diger: 0,
    androidKullanici: 0, iosKullanici: 0,
  });

  const secilenEkran = useMemo(
    () => EKRAN_SECENEKLERI.find((o) => o.v === ekran) || null,
    [ekran]
  );

  // Tip değişince varsayılan ekranı öner; admin ekranı elle seçtiyse ezme.
  const handleTipChange = (yeniTip) => {
    setBildirimTipi(yeniTip);
    const varsayilan = TIP_VARSAYILAN_EKRAN[yeniTip];
    const oncekiVarsayilan = TIP_VARSAYILAN_EKRAN[bildirimTipi];
    if (varsayilan && (!ekran || ekran === oncekiVarsayilan)) {
      setEkran(varsayilan);
      setIlgiliId("");
    }
  };

  const setSuccessTemp = (m) => {
    setSuccessMsg(m);
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  // ---- Token istatistikleri (toplam aktif token) ----
  // Sayım sunucuda ($count) yapılıyor; eski $top=2000 kırpması tabloyu yarıda
  // kesip Android/iOS dağılımını yanlış gösteriyordu.
  const fetchTokenStats = async () => {
    try {
      const s = await fetchPlatformStats();
      setTokenStats({
        total: s.toplamToken,
        web: s.web,
        android: s.android,
        ios: s.ios,
        diger: s.diger,
        androidKullanici: s.androidKullanici,
        iosKullanici: s.iosKullanici,
      });
    } catch (e) {
      // sessizce geç
    }
  };

  // ---- Kategori listesi ----
  const fetchKategoriler = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/KATEGORILER?$select=ID,AD&$orderby=AD&$top=200`,
        { cache: "no-store" }
      );
      const json = await res.json();
      setKategoriler(json?.value || []);
    } catch {}
  };

  useEffect(() => {
    fetchTokenStats();
    fetchKategoriler();
  }, []);

  // ---- Kullanıcı arama (debounced) ----
  useEffect(() => {
    if (hedefTip !== "user" || aramaSorgu.length < 2) {
      setAramaSonuc([]);
      return;
    }
    const t = setTimeout(async () => {
      setAramaLoading(true);
      try {
        // ' kaçışı + URL kodlama: "&", "#", "+" içeren aramalar sorguyu bozuyordu.
        const esc = v => encodeURIComponent(v.replace(/'/g, "''"));
        const q = esc(aramaSorgu.trim());
        const qLower = esc(aramaSorgu.trim().toLocaleLowerCase("tr"));
        const res = await fetch(
          `${BASE_URL}/KULLANICILAR?$filter=contains(tolower(AD_SOYAD),'${qLower}') or contains(CEP_NO,'${q}') or contains(tolower(Mail_Adress),'${qLower}')&$top=10&$select=ID,AD_SOYAD,Mail_Adress,CEP_NO`,
          { cache: "no-store" }
        );
        const json = await res.json();
        setAramaSonuc(json?.value || []);
      } catch {
        setAramaSonuc([]);
      } finally {
        setAramaLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [aramaSorgu, hedefTip]);

  // ---- Hedef kitle ID listesini hesapla ----
  const resolveTargetIds = async () => {
    if (hedefTip === "user") {
      return seciliKullanici ? [seciliKullanici.ID] : [];
    }
    if (hedefTip === "tokenholders") {
      const res = await fetch(
        `${BASE_URL}/KULLANICI_FCM_TOKENLERI?$filter=AKTIF eq true&$select=KULLANICI_ID&$top=5000`,
        { cache: "no-store" }
      );
      const json = await res.json();
      return Array.from(new Set((json?.value || []).map((x) => Number(x.KULLANICI_ID)).filter(Boolean)));
    }
    if (hedefTip === "category") {
      if (!seciliKategoriId) return [];
      const res = await fetch(
        `${BASE_URL}/KATEGORI_ABONELIKLERI?$filter=KATEGORI_ID eq ${seciliKategoriId}&$select=KULLANICI_ID&$top=5000`,
        { cache: "no-store" }
      );
      const json = await res.json();
      return Array.from(new Set((json?.value || []).map((x) => Number(x.KULLANICI_ID)).filter(Boolean)));
    }
    // all — silinen hesaplar (AKTIF_DURUM='Silindi', HesapController konvansiyonu)
    // hedef kitleye girmemeli; aksi halde anonimleştirilmiş kayıtlara boşuna log açılır.
    const res = await fetch(
      `${BASE_URL}/KULLANICILAR?$select=ID&$filter=(AKTIF_DURUM eq null or AKTIF_DURUM ne 'Silindi')&$top=10000`,
      { cache: "no-store" }
    );
    const json = await res.json();
    return Array.from(new Set((json?.value || []).map((x) => Number(x.ID)).filter(Boolean)));
  };

  // Sunucu tarafı toplu uçlara gönderilen ortak gövde.
  // Alan adları AdminBildirimController.TopluBildirimIstek ile birebir.
  const topluIstekGovdesi = () => ({
    HedefTip: hedefTip,
    KategoriId: hedefTip === "category" && seciliKategoriId ? Number(seciliKategoriId) : null,
    KullaniciId: hedefTip === "user" && seciliKullanici ? Number(seciliKullanici.ID) : null,
    Baslik: baslik,
    Icerik: mesaj,
    Tip: bildirimTipi,
    Ekran: ekran || null,
    IlgiliId: ilgiliId ? Number(ilgiliId) : null,
  });

  // Sunucudan gelen hata gövdesini okunur mesaja çevirir (ModelState / message / düz metin).
  const hataMesajiCoz = async (res, varsayilan) => {
    try {
      const govde = await res.json();
      return govde?.Message || govde?.message || govde?.ExceptionMessage || varsayilan;
    } catch {
      return varsayilan;
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      // Yeni yol: sayım sunucuda; 10.000 satırlık ID listesi tarayıcıya inmez.
      const res = await fetch(`${BASE_URL}${TOPLU_UC}/HedefSayisi`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(topluIstekGovdesi()),
      });
      if (res.ok) {
        const json = await res.json();
        setPreviewCount(json?.kisi ?? 0);
        setPreviewTokenCount(json?.token ?? null);
        return;
      }
      if (res.status !== 404) {
        setError(await hataMesajiCoz(res, "Hedef kitle hesaplanamadı."));
        return;
      }
      // 404 = API henüz deploy edilmemiş; eski OData sayımına düş.
      const ids = await resolveTargetIds();
      setPreviewCount(ids.length);
      setPreviewTokenCount(null);
    } catch (e) {
      setError("Hedef kitle hesaplanamadı.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!baslik.trim()) return setError("Başlık zorunlu.");
    if (!mesaj.trim()) return setError("Mesaj zorunlu.");
    if (hedefTip === "user" && !seciliKullanici) return setError("Bir kullanıcı seçin.");
    if (hedefTip === "category" && !seciliKategoriId) return setError("Bir kategori seçin.");
    // ID isteyen ekran seçildiyse ILGILI_ID zorunlu — yoksa bildirime tıklayınca boş ekran açılır.
    if (secilenEkran?.idAlani && !ilgiliId)
      return setError(`${secilenEkran.l} için ${secilenEkran.idAlani} girin.`);

    if (!window.confirm("Bildirim gönderilecek. Devam edilsin mi?")) return;

    setSending(true);
    try {
      // --- Yeni yol: tek istek. Sunucu hedefi çözer, logları toplu yazar,
      // push'u arka plana kuyruklar. Sekme kapansa bile gönderim sürer.
      const res = await fetch(`${BASE_URL}${TOPLU_UC}/TopluGonder`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(topluIstekGovdesi()),
      });

      if (res.ok) {
        const json = await res.json();
        setSuccessTemp(
          `${json?.hedefKisi ?? 0} kişiye bildirim yazıldı. Push arka planda gönderiliyor.`
        );
        setBaslik("");
        setMesaj("");
        setIlgiliId("");
        setPreviewCount(null);
        setPreviewTokenCount(null);
        if (activeTab === "history") fetchGecmis();
        return;
      }

      if (res.status !== 404) {
        setError(await hataMesajiCoz(res, "Gönderim başarısız."));
        return;
      }

      // --- 404: toplu uç henüz canlıda yok. Eski kullanıcı-başına-POST akışı.
      // API deploy edildikten sonra bu dal hiç çalışmaz.
      const ids = await resolveTargetIds();
      if (ids.length === 0) {
        setError("Hedef kitlede kullanıcı bulunamadı.");
        return;
      }
      const now = new Date().toISOString();
      // Toplu insert — küçük gruplar halinde paralel
      const CHUNK = 20;
      let success = 0;
      let failed = 0;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        const results = await Promise.allSettled(
          slice.map((uid) =>
            fetch(`${BASE_URL}/BILDIRIM_LOGLARI`, {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                // Backend admin rolünde KULLANICI_ID'yi korur (override etmez).
                KULLANICI_ID: uid,
                BASLIK: baslik,
                ICERIK: mesaj, // BILDIRIM_LOGLARI tablosundaki sütun adı ICERIK (MESAJ değil)
                TIP: bildirimTipi,
                // EKRAN boşsa kolonu hiç göndermeyip backend varsayılanına bırak.
                ...(ekran ? { EKRAN: ekran } : {}),
                ...(ilgiliId ? { ILGILI_ID: Number(ilgiliId) } : {}),
                OKUNDU: false,
                GONDERIM_TARIHI: now,
              }),
            })
          )
        );
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value.ok) success++;
          else failed++;
        });
      }
      setSuccessTemp(
        `Bildirim gönderildi. ${success} başarılı${failed ? `, ${failed} başarısız` : ""}.`
      );
      setBaslik("");
      setMesaj("");
      setIlgiliId("");
      setPreviewCount(null);
      setPreviewTokenCount(null);
      if (activeTab === "history") fetchGecmis();
    } catch (e) {
      setError(e.message || "Gönderim başarısız.");
    } finally {
      setSending(false);
    }
  };

  // ---- Geçmiş ----
  const fetchGecmis = async () => {
    setGecmisLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/BILDIRIM_LOGLARI?$top=200&$orderby=GONDERIM_TARIHI desc`,
        { cache: "no-store", headers: getAuthHeaders() }
      );
      const json = await res.json();
      setGecmis(json?.value || []);
    } catch {
      setGecmis([]);
    } finally {
      setGecmisLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history" && gecmis.length === 0) fetchGecmis();
  }, [activeTab]); // eslint-disable-line

  const gecmisColumns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Alıcı",
      accessorKey: "KULLANICI_ID",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => <span className="text-muted">#{cell.getValue()}</span>,
    },
    {
      header: "Başlık",
      accessorKey: "BASLIK",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => <strong>{cell.getValue()}</strong>,
    },
    {
      header: "Mesaj",
      accessorKey: "ICERIK",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <span style={{ maxWidth: 300, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={cell.getValue()}>
          {cell.getValue()}
        </span>
      ),
    },
    {
      header: "Tip",
      accessorKey: "TIP",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => {
        const t = cell.getValue() || "SistemBildirim";
        // BildirimTipleri.cs ile uyumlu renk haritası — mobil/web NotificationsScreen ile aynı kategoriler
        const colorMap = {
          // Sipariş & İade & Kargo & Finans → mavi
          YeniSiparis: "info", SiparisTeyit: "info", SiparisDurum: "info", SiparisIptalSatici: "info",
          SistemSiparisi: "info", KargoDurum: "info", YeniIade: "info", IadeTeyit: "info",
          IadeDurum: "info", IadeDurumSatici: "info", Finans: "info",
          // Mesaj & Soru → yeşil
          YeniSoru: "success", SoruTeyit: "success", SoruCevaplandi: "success",
          SistemSorusu: "success", MagazaSoru: "success", MagazaSoruCevap: "success",
          // İlan teklifleri → camgöbeği
          IlanTeklif: "primary", TeklifCevap: "primary", TeklifTeyit: "primary",
          TeklifGuncellendi: "primary", TeklifRed: "primary", TeklifKazandi: "primary",
          // Mezat → turuncu
          MezatTeklif: "warning", CanliYayin: "warning",
          // Değerleme → eflatun
          Degerleme: "warning", YeniDegerleme: "warning", DegerlemeTeyit: "warning", DegerlemeSonuc: "warning",
          DegerlemeSureBitiyor: "warning", DegerlemeSuresiDoldu: "warning",
          DegerlemeSonucSahibi: "warning", DegerlemeTalepSahibi: "warning",
          // İlan etkileşimleri
          YeniIlan: "primary", YeniYorum: "primary", YeniBegenme: "primary",
          FavoriEklendi: "primary", YeniTakipci: "primary", MagazaTakip: "primary",
          // Mağaza
          MagazaBasvuru: "info", MagazaDurumGuncelleme: "info",
          // Sistem
          SistemBildirim: "secondary", YeniIcerik: "primary",
          // Eski legacy (geriye dönük uyumluluk)
          Bilgi: "info", Uyarı: "warning", Önemli: "danger", Promosyon: "success",
        };
        return <Badge color={colorMap[t] || "secondary"}>{t}</Badge>;
      },
    },
    {
      header: "Okundu",
      accessorKey: "OKUNDU",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) =>
        cell.getValue() ? <Badge color="success">Evet</Badge> : <Badge color="secondary">Hayır</Badge>,
    },
    {
      header: "Tarih",
      accessorKey: "GONDERIM_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleString("tr-TR") : "-";
      },
    },
  ], []);

  const hedefAciklama = useMemo(() => {
    if (hedefTip === "all") return "Sistemdeki tüm kullanıcılara";
    if (hedefTip === "user") return seciliKullanici ? `${seciliKullanici.AD_SOYAD} (#${seciliKullanici.ID})` : "Bir kullanıcı seçilmedi";
    if (hedefTip === "category") {
      const k = kategoriler.find((x) => String(x.ID) === String(seciliKategoriId));
      return k ? `${k.AD} kategorisi abonelerine` : "Bir kategori seçilmedi";
    }
    if (hedefTip === "tokenholders") return "Bildirim açık tüm cihazlara";
    return "-";
  }, [hedefTip, seciliKullanici, seciliKategoriId, kategoriler]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Bildirim Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {/* TOKEN İSTATİSTİKLERİ */}
          <Row className="mb-3">
            <Col md={3}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm bg-primary bg-soft rounded-circle d-flex align-items-center justify-content-center me-3">
                      <i className="bx bx-bell text-primary fs-3" />
                    </div>
                    <div>
                      <h5 className="mb-0">{tokenStats.total}</h5>
                      <small className="text-muted">Aktif Token</small>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card><CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-info bg-soft rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bx-globe text-info fs-3" />
                  </div>
                  <div>
                    <h5 className="mb-0">{tokenStats.web}</h5>
                    <small className="text-muted">Web</small>
                  </div>
                </div>
              </CardBody></Card>
            </Col>
            <Col md={3}>
              <Card><CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-success bg-soft rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bxl-android text-success fs-3" />
                  </div>
                  <div>
                    <h5 className="mb-0">{tokenStats.android}</h5>
                    <small className="text-muted">
                      Android · {tokenStats.androidKullanici} kullanıcı
                    </small>
                  </div>
                </div>
              </CardBody></Card>
            </Col>
            <Col md={3}>
              <Card><CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-secondary bg-soft rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bxl-apple text-secondary fs-3" />
                  </div>
                  <div>
                    <h5 className="mb-0">{tokenStats.ios}</h5>
                    <small className="text-muted">
                      iOS · {tokenStats.iosKullanici} kullanıcı
                    </small>
                  </div>
                </div>
              </CardBody></Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <Nav tabs className="nav-tabs-custom mb-3">
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "compose" })}
                    onClick={() => setActiveTab("compose")} style={{ cursor: "pointer" }}>
                    <i className="bx bx-send me-1" />Bildirim Gönder
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={classnames({ active: activeTab === "history" })}
                    onClick={() => setActiveTab("history")} style={{ cursor: "pointer" }}>
                    <i className="bx bx-history me-1" />Gönderim Geçmişi
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                {/* ---- COMPOSE ---- */}
                <TabPane tabId="compose">
                  <Form onSubmit={handleSend}>
                    <Row>
                      <Col lg={8}>
                        <FormGroup>
                          <Label className="fw-medium">Hedef Kitle</Label>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {[
                              { v: "all", l: "Tüm Kullanıcılar", i: "bx-group" },
                              { v: "tokenholders", l: "Bildirim Açık Olanlar", i: "bx-bell" },
                              { v: "category", l: "Kategori Abonesi", i: "bx-category" },
                              { v: "user", l: "Belirli Kullanıcı", i: "bx-user" },
                            ].map((opt) => (
                              <Button
                                key={opt.v}
                                color={hedefTip === opt.v ? "primary" : "light"}
                                size="sm"
                                onClick={(e) => { e.preventDefault(); setHedefTip(opt.v); setPreviewCount(null); setPreviewTokenCount(null); }}
                              >
                                <i className={`bx ${opt.i} me-1`} />{opt.l}
                              </Button>
                            ))}
                          </div>

                          {hedefTip === "category" && (
                            <Input type="select" value={seciliKategoriId} onChange={(e) => { setSeciliKategoriId(e.target.value); setPreviewCount(null); setPreviewTokenCount(null); }}>
                              <option value="">— Kategori seçin —</option>
                              {kategoriler.map((k) => (
                                <option key={k.ID} value={k.ID}>{k.AD}</option>
                              ))}
                            </Input>
                          )}

                          {hedefTip === "user" && (
                            <>
                              <Input
                                placeholder="Ad, telefon veya e-posta ile ara..."
                                value={aramaSorgu}
                                onChange={(e) => setAramaSorgu(e.target.value)}
                              />
                              {aramaLoading && <small className="text-muted">Aranıyor...</small>}
                              {aramaSonuc.length > 0 && (
                                <div className="border rounded mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                                  {aramaSonuc.map((u) => (
                                    <div
                                      key={u.ID}
                                      className={`p-2 d-flex justify-content-between align-items-center ${seciliKullanici?.ID === u.ID ? "bg-primary bg-soft" : ""}`}
                                      style={{ cursor: "pointer", borderBottom: "1px solid #eee" }}
                                      onClick={() => { setSeciliKullanici(u); setAramaSonuc([]); setAramaSorgu(u.AD_SOYAD); setPreviewCount(null); setPreviewTokenCount(null); }}
                                    >
                                      <div>
                                        <div className="fw-medium">{u.AD_SOYAD}</div>
                                        <small className="text-muted">{u.Mail_Adress || u.CEP_NO}</small>
                                      </div>
                                      <small>#{u.ID}</small>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {seciliKullanici && (
                                <div className="mt-2">
                                  <Badge color="primary" className="p-2">
                                    Seçili: {seciliKullanici.AD_SOYAD} (#{seciliKullanici.ID})
                                    <i className="bx bx-x ms-2" style={{ cursor: "pointer" }}
                                       onClick={() => { setSeciliKullanici(null); setAramaSorgu(""); setPreviewCount(null); setPreviewTokenCount(null); }} />
                                  </Badge>
                                </div>
                              )}
                            </>
                          )}

                          <div className="mt-2 p-2 bg-light rounded">
                            <small className="text-muted">Hedef:</small> <strong>{hedefAciklama}</strong>
                            <Button color="link" size="sm" className="ms-2" onClick={(e) => { e.preventDefault(); handlePreview(); }} disabled={previewLoading}>
                              {previewLoading ? <Spinner size="sm" /> : "Hedef Kişi Sayısını Hesapla"}
                            </Button>
                            {previewCount !== null && <Badge color="info" className="ms-2">{previewCount} kişi</Badge>}
                            {previewTokenCount !== null && (
                              <Badge color="success" className="ms-1">{previewTokenCount} cihaz</Badge>
                            )}
                          </div>
                        </FormGroup>

                        <FormGroup>
                          <Label className="fw-medium">Başlık <span className="text-danger">*</span></Label>
                          <Input
                            value={baslik}
                            onChange={(e) => setBaslik(e.target.value)}
                            placeholder="Bildirim başlığı"
                            maxLength={120}
                            required
                          />
                          <small className="text-muted">{baslik.length}/120 karakter</small>
                        </FormGroup>

                        <FormGroup>
                          <Label className="fw-medium">Mesaj <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            rows={5}
                            value={mesaj}
                            onChange={(e) => setMesaj(e.target.value)}
                            placeholder="Kısa, öz bir mesaj..."
                            maxLength={400}
                            required
                          />
                          <small className="text-muted">{mesaj.length}/400 karakter</small>
                        </FormGroup>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label className="fw-medium">Tip</Label>
                              <Input type="select" value={bildirimTipi} onChange={(e) => handleTipChange(e.target.value)}>
                                <optgroup label="Sistem">
                                  <option value="SistemBildirim">Sistem Bildirimi</option>
                                  <option value="YeniIcerik">Duyuru / İçerik</option>
                                  <option value="Finans">Finans</option>
                                </optgroup>
                                <optgroup label="Değerleme">
                                  <option value="Degerleme">Değerleme Duyurusu</option>
                                  <option value="DegerlemeSonuc">Değerleme Sonucu</option>
                                  <option value="YeniDegerleme">Yeni Değerleme Talebi</option>
                                </optgroup>
                                <optgroup label="Sipariş">
                                  <option value="YeniSiparis">Yeni Sipariş</option>
                                  <option value="SiparisDurum">Sipariş Durum</option>
                                  <option value="KargoDurum">Kargo Durum</option>
                                </optgroup>
                                <optgroup label="İlan / Kampanya">
                                  <option value="YeniIlan">Yeni İlan</option>
                                </optgroup>
                              </Input>
                              <small className="text-muted">Tip, mobil ve web bildirim merkezindeki ikon ve yönlendirmeyi belirler.</small>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label className="fw-medium">Tıklanınca Açılacak Ekran (opsiyonel)</Label>
                              <Input type="select" value={ekran} onChange={(e) => { setEkran(e.target.value); setIlgiliId(""); }}>
                                {EKRAN_SECENEKLERI.map((o) => (
                                  <option key={o.v || "default"} value={o.v}>{o.l}</option>
                                ))}
                              </Input>
                              <small className="text-muted">
                                BILDIRIM_LOGLARI.EKRAN kolonuna yazılır; push&apos;ta data.screen olarak gider.
                              </small>
                            </FormGroup>
                            {secilenEkran?.idAlani && (
                              <FormGroup>
                                <Label className="fw-medium">{secilenEkran.idAlani} <span className="text-danger">*</span></Label>
                                <Input
                                  type="number"
                                  value={ilgiliId}
                                  onChange={(e) => setIlgiliId(e.target.value)}
                                  placeholder={`Örn. 1234 (${secilenEkran.idAlani})`}
                                />
                                <small className="text-muted">ILGILI_ID kolonuna yazılır; bu ekran ID olmadan açılmaz.</small>
                              </FormGroup>
                            )}
                          </Col>
                        </Row>

                        <div className="d-flex gap-2 mt-3">
                          <Button color="primary" type="submit" disabled={sending} size="lg">
                            {sending ? <><Spinner size="sm" className="me-2" />Gönderiliyor...</> : <><i className="bx bx-send me-1" />Bildirim Gönder</>}
                          </Button>
                          <Button color="secondary" outline type="button" onClick={() => {
                            setBaslik(""); setMesaj(""); setIlgiliId(""); setPreviewCount(null); setPreviewTokenCount(null);
                          }} disabled={sending}>
                            Temizle
                          </Button>
                        </div>
                      </Col>

                      {/* PREVIEW PANEL */}
                      <Col lg={4}>
                        <div className="border rounded p-3 bg-light h-100">
                          <h6 className="text-muted mb-3"><i className="bx bx-mobile me-1" />Önizleme</h6>
                          <div className="bg-white rounded p-3 shadow-sm" style={{ borderLeft: "4px solid #556ee6" }}>
                            <div className="d-flex align-items-start gap-2">
                              <div className="bg-primary bg-soft text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                                <i className="bx bx-bell" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ fontSize: 14 }}>{baslik || "Başlık burada görünecek"}</strong>
                                <p className="mb-1" style={{ fontSize: 13, color: "#555" }}>
                                  {mesaj || "Mesaj burada görünecek..."}
                                </p>
                                <small className="text-muted">Şimdi · HarmanaGEL</small>
                              </div>
                            </div>
                          </div>
                          <small className="text-muted d-block mt-3">
                            <i className="bx bx-info-circle me-1" />
                            Gönderim tek istekle API&apos;ye iletilir: BILDIRIM_LOGLARI satırları toplu yazılır,
                            FCM push arka planda gönderilir. Bu sayfayı kapatabilirsiniz.
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </TabPane>

                {/* ---- HISTORY ---- */}
                <TabPane tabId="history">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Son Gönderimler</h5>
                    <Button color="primary" size="sm" onClick={fetchGecmis} disabled={gecmisLoading}>
                      <i className="bx bx-refresh me-1" />Yenile
                    </Button>
                  </div>
                  {gecmisLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={gecmisColumns}
                      data={gecmis}
                      isGlobalFilter={true}
                      SearchPlaceholder="Bildirim ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BildirimGonder;
